// Mirrors apps/api/docs/contracts/prospecting.md.

export type CandidateStatus = 'new' | 'accepted' | 'rejected'

export type CrmSyncStatus =
  | 'creating'
  | 'created'
  | 'linked'
  | 'pending_approval'
  | 'disabled'
  | 'not_connected'
  | 'failed'
  | 'dismissed'

export type CandidateCrmError = 'hubspot_error' | 'hubspot_reauth_required' | 'field_write_denied'

export interface ProspectCandidate {
  id: string
  name: string
  domain: string
  normalizedDomain: string
  fitReasoning: string
  likelyTitles: string[]
  sourceUrls: string[]
  location: string | null
  headcountRange: string | null
  industry: string | null
  status: CandidateStatus
  runId: string
  surfacedBy: string
  reviewedBy: string | null
  reviewedAt: string | null
  rejectReason: string | null
  crmSync: CrmSyncStatus | null
  hubspotCompanyId: string | null
  crmError: CandidateCrmError | null
  createdAt: string
  updatedAt: string
}

export interface CandidatePage {
  candidates: ProspectCandidate[]
  nextCursor: string | null
}

export type RunStatus = 'queued' | 'running' | 'completed' | 'failed'

export type RunStage = 'queued' | 'queries_generated' | 'searching' | 'extracted' | 'verifying' | 'done'

/** The subset of the run object the review page uses. */
export interface ProspectingRun {
  id: string
  status: RunStatus
  stage: RunStage
  stopReason: 'target_reached' | 'usage_cap_reached' | 'exhausted_results' | null
  candidates: { candidateId: string }[]
  error: { code: string; message: string } | null
}

/** States from which a member can still approve or dismiss the HubSpot write. */
export const CRM_SYNC_ACTIONABLE: readonly CrmSyncStatus[] = ['pending_approval', 'failed', 'not_connected']

// --- Owner/Admin usage view (ZAI-393) ------------------------------------------

export type WorkspaceRole = 'owner' | 'admin' | 'member'

/** `cap` is null when nothing caps the resource at this level. */
export interface UsageMeter {
  used: number
  cap: number | null
}

/** Keyed by resource so a new one (e.g. vendor credit spend) is a new key. */
export interface ProspectingUsageMeters {
  searches: UsageMeter
  modelCalls: UsageMeter
  tokens: UsageMeter
}

export interface ProspectingActivity {
  runs: { total: number; completed: number; failed: number; inProgress: number }
  candidates: { found: number; accepted: number; rejected: number; pending: number }
  deduped: { total: number; existingCandidate: number; crmCompany: number; crmContact: number }
  meters: ProspectingUsageMeters
}

export interface ProspectingRepUsage extends ProspectingActivity {
  userId: string
  name: string | null
  email: string | null
  role: WorkspaceRole | null
}

export interface ProspectingUsage {
  period: { start: string; end: string }
  workspace: ProspectingActivity
  reps: ProspectingRepUsage[]
}
